import { supabase } from '@/lib/supabaseClient';
import { notificationService } from './notificationService';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { apiClient } from '@/lib/apiClient';

interface ProjectEvent {
  type: 'project_created' | 'project_assigned' | 'stage_changed' | 'pipeline_entered' | 'design_request_created' | 'design_request_status_changed';
  project: any;
  metadata?: {
    from_stage_id?: number;
    to_stage_id?: number;
    team_id?: string;
    pipeline_id?: number;
    user_id?: string;
    design_request_id?: string;
    design_request_status?: string;
    design_request_type?: string;
  };
}

interface AutomationStep {
  id: string;
  type: 'trigger' | 'action';
  triggerType?: string;
  actionType?: string;
  config: any;
  position: number;
}

interface Automation {
  id: string;
  name: string;
  is_active: boolean;
  steps: AutomationStep[];
}

export class FollowerAutomationService {
  /**
   * Execute follower automations for a project event
   */
  async executeFollowerAutomations(event: ProjectEvent) {
    try {
      // Get all active automations
      const automations = await this.getActiveFollowerAutomations();
      
      for (const automation of automations) {
        if (await this.matchesTrigger(automation, event)) {
          await this.executeAutomation(automation, event);
        }
      }
    } catch (error) {
      console.error('Error executing follower automations:', error);
    }
  }

  /**
   * Get all active automations that have follower-related actions
   */
  private async getActiveFollowerAutomations(): Promise<Automation[]> {
    const { data, error } = await supabase
      .from('automations')
      .select('*')
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching automations:', error);
      return [];
    }

    // Return all active automations (not just follower actions, as we now support multiple action types)
    return data || [];
  }

  /**
   * Check if an automation's trigger matches the event
   */
  private async matchesTrigger(automation: Automation, event: ProjectEvent): Promise<boolean> {
    const trigger = automation.steps.find(step => step.type === 'trigger');
    if (!trigger || !trigger.config) {
      return false;
    }

    const config = trigger.config;
    
    // Check trigger type matches event type
    if (trigger.triggerType !== event.type) return false;

    // Check pipeline filter
    if (config.filters?.pipeline_ids?.length > 0) {
      if (!config.filters.pipeline_ids.includes(event.project.pipeline_id)) {
        return false;
      }
    }

    // Check team filter (for project_assigned)
    if (event.type === 'project_assigned' && config.filters?.team_ids?.length > 0) {
      if (!event.metadata?.team_id || !config.filters.team_ids.includes(event.metadata.team_id)) {
        return false;
      }
    }

    // Check stage filters (for stage_changed)
    if (event.type === 'stage_changed') {
      if (config.filters?.from_stage_ids?.length > 0 && event.metadata?.from_stage_id) {
        if (!config.filters.from_stage_ids.includes(event.metadata.from_stage_id)) {
          return false;
        }
      }
      if (config.filters?.to_stage_ids?.length > 0 && event.metadata?.to_stage_id) {
        if (!config.filters.to_stage_ids.includes(event.metadata.to_stage_id)) {
          return false;
        }
      }
    }

    // Check design request filters (for design_request_created and design_request_status_changed)
    if (event.type === 'design_request_created' || event.type === 'design_request_status_changed') {
      // Check request type filter
      if (config.filters?.request_type?.length > 0 && event.metadata?.design_request_type) {
        if (!config.filters.request_type.includes(event.metadata.design_request_type)) {
          return false;
        }
      }
      
      // Check status filter (for status changed events)
      if (event.type === 'design_request_status_changed' && config.filters?.status?.length > 0 && event.metadata?.design_request_status) {
        if (!config.filters.status.includes(event.metadata.design_request_status)) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Execute an automation for a project event
   */
  private async executeAutomation(automation: Automation, event: ProjectEvent) {
    try {
      // Log execution start
      await this.logExecution(automation.id, event, 'running');

      // Execute all action steps
      const actionSteps = automation.steps
        .filter(step => step.type === 'action')
        .sort((a, b) => a.position - b.position);

      for (const step of actionSteps) {
        if (step.actionType === 'add_followers') {
          await this.executeAddFollowersAction(step.config, event.project);
        } else if (step.actionType === 'notification') {
          await this.executeSendNotificationAction(step.config, event.project);
        } else if (step.actionType === 'update_stage') {
          await this.executeUpdateStageAction(step.config, event.project);
        }
        // Handle other action types as needed
      }

      // Log execution success
      await this.logExecution(automation.id, event, 'completed');
    } catch (error) {
      console.error('Error executing automation:', error);
      await this.logExecution(automation.id, event, 'failed', error);
    }
  }

  /**
   * Execute the add followers action
   */
  private async executeAddFollowersAction(config: any, project: any) {
    const followersToAdd = await this.getFollowersToAdd(config, project);
    
    for (const userId of followersToAdd) {
      await this.addProjectFollower(project.id, userId);
      
      if (config.notify_on_add) {
        await this.notifyNewFollower(userId, project);
      }
    }
  }

  /**
   * Get list of user IDs to add as followers based on configuration
   */
  private async getFollowersToAdd(config: any, project: any): Promise<string[]> {
    const userIds = new Set<string>();

    // Add specific users
    if (config.follower_source === 'specific_users' && config.user_ids?.length > 0) {
      config.user_ids.forEach((id: string) => userIds.add(id));
    }

    // Add team members
    if (config.follower_source === 'team_members' && config.team_ids?.length > 0) {
      const { data: teamMembers } = await supabase
        .from('user_teams')
        .select('user_id')
        .in('team_id', config.team_ids);
      
      if (teamMembers) {
        teamMembers.forEach(member => userIds.add(member.user_id));
      }
    }

    // Add users with specific roles
    if (config.follower_source === 'role_based' && config.role_ids?.length > 0) {
      const { data: roleUsers } = await supabase
        .from('user_roles')
        .select('user_id')
        .in('role_id', config.role_ids);
      
      if (roleUsers) {
        roleUsers.forEach(user => userIds.add(user.user_id));
      }
    }

    // Add project owner's team
    if (config.follower_source === 'project_owner_team' || config.include_project_owner_team) {
      // Get project owner's team
      if (project.sales_rep_id) {
        const { data: userTeam } = await supabase
          .from('user_teams')
          .select('team_id')
          .eq('user_id', project.sales_rep_id)
          .single();
        
        if (userTeam) {
          const { data: teamMembers } = await supabase
            .from('user_teams')
            .select('user_id')
            .eq('team_id', userTeam.team_id);
          
          if (teamMembers) {
            teamMembers.forEach(member => userIds.add(member.user_id));
          }
        }
      }
    }

    // Remove users who are already followers
    const { data: existingFollowers } = await supabase
      .from('project_followers')
      .select('user_id')
      .eq('related_entity_id', project.id)
      .eq('related_entity_type', 'project');
    
    if (existingFollowers) {
      existingFollowers.forEach(follower => userIds.delete(follower.user_id));
    }

    return Array.from(userIds);
  }

  /**
   * Add a user as a project follower
   */
  private async addProjectFollower(projectId: string, userId: string) {
    // Get the tenant_id dynamically
    const tenantId = await getCurrentTenantId();
    
    if (!tenantId) {
      throw new Error('No active tenant found');
    }

    const { error } = await supabase
      .from('project_followers')
      .insert({
        related_entity_type: 'project',
        related_entity_id: projectId,
        user_id: userId,
        tenant_id: tenantId
      });

    if (error) {
      console.error('Error adding project follower:', error);
      throw error;
    }
  }

  /**
   * Notify a user that they've been added as a follower
   */
  private async notifyNewFollower(userId: string, project: any) {
    await notificationService.createNotification({
      user_id: userId,
      type: 'project_follower',
      title: 'Added as Project Follower',
      message: `You have been automatically added as a follower to project: ${project.customer_name || 'Unnamed Project'}`,
      project_id: project.id,
      project_name: project.customer_name,
      project_type: project.project_type,
      link: `/app/${project.project_type === 'Residential' ? 'pipeline-resi' : 'pipeline'}?pipelineId=${project.pipeline_id}&projectId=${project.id}&modal=true`
    });
  }

  /**
   * Execute the send notification action
   */
  private async executeSendNotificationAction(config: any, project: any) {
    const recipientUserIds = new Set<string>();

    // Get followers if needed
    if (config.send_to_followers) {
      const { data: followers } = await supabase
        .from('project_followers')
        .select('user_id')
        .eq('related_entity_id', project.id)
        .eq('related_entity_type', 'project');
      
      if (followers) {
        followers.forEach(f => recipientUserIds.add(f.user_id));
      }
    }

    // Add assignee if needed
    if (config.send_to_assignee && project.assigned_to) {
      recipientUserIds.add(project.assigned_to);
    }

    // Add owner if needed
    if (config.send_to_owner && project.sales_rep_id) {
      recipientUserIds.add(project.sales_rep_id);
    }

    // Send notification to each recipient
    for (const userId of recipientUserIds) {
      await notificationService.createNotification({
        user_id: userId,
        type: 'project_stage_move',  // Use a valid notification type that exists in the enum
        title: config.title || 'Automation Notification',
        message: config.message || 'An automation was triggered for your project',
        project_id: project.id,
        project_name: project.customer_name,
        project_type: project.project_type,
        link: `/app/${project.project_type === 'Residential' ? 'pipeline-resi' : 'pipeline'}?pipelineId=${project.pipeline_id}&projectId=${project.id}&modal=true`,
        skipEmail: true,  // Automation notifications should only be in-app, not email
        metadata: {
          source: 'automation',  // Track that this came from automation
          priority: config.priority || 'normal'
        }
      });
    }
  }

  /**
   * Execute the update stage action
   */
  private async executeUpdateStageAction(config: any, project: any) {
    try {
      let targetStageId: number | null = null;

      // If using stage name, find the stage ID
      if (config.use_stage_name && config.stage_name) {
        const { data: stages, error: stageError } = await supabase
          .from('stages')
          .select('id, name')
          .eq('pipeline_id', project.pipeline_id);
        
        if (stageError) {
          console.error('Error fetching stages:', stageError);
          return;
        }
        
        const stage = stages?.find(s => 
          s.name.toLowerCase() === config.stage_name.toLowerCase()
        );
        
        if (stage) {
          targetStageId = stage.id;
        } else {
          console.warn(`Stage "${config.stage_name}" not found in pipeline ${project.pipeline_id}`);
          return;
        }
      } else if (config.stage_id) {
        targetStageId = config.stage_id;
      }

      if (!targetStageId) {
        console.warn('No target stage specified for update_stage action');
        return;
      }

      // Update the project's stage
      const { data: updatedProject, error } = await supabase
        .from('projects')
        .update({ 
          current_stage_id: targetStageId,
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating project stage:', error);
        throw error;
      }

    } catch (error) {
      console.error('Error executing update stage action:', error);
      throw error;
    }
  }

  /**
   * Log automation execution for audit trail
   */
  private async logExecution(
    automationId: string, 
    event: ProjectEvent, 
    status: 'running' | 'completed' | 'failed',  // Changed 'started' to 'running'
    error?: any
  ) {
    try {
      // Get the tenant_id dynamically
      const tenantId = await getCurrentTenantId();
      
      if (!tenantId) {
        console.warn('No active tenant found for automation execution logging');
        return;
      }

      // Check if automation_executions table exists by trying to query it
      const { data: tableCheck } = await supabase
        .from('automation_executions')
        .select('id')
        .limit(0);
      
      // If no error from the above query, table exists, so insert the log
      if (tableCheck !== null) {
        // Map status to database schema values: 'active', 'completed', 'failed', 'cancelled'
        const dbStatus = status === 'running' ? 'active' : status;

        const { error: logError } = await supabase
          .from('automation_executions')
          .insert({
            automation_id: automationId,
            project_id: event.project.id,
            tenant_id: tenantId,
            status: dbStatus,
            trigger_data: {
              event_type: event.type,
              metadata: event.metadata
            },
            error_message: error ? String(error) : null
          });

        if (logError) {
          console.error('Error logging automation execution:', logError);
          // Don't throw, just log - we don't want to fail the automation because of logging
        }
      }
    } catch (err) {
      // Table might not exist, just log and continue
    }
  }

  /**
   * Trigger follower automations when a project is created
   */
  async onProjectCreated(project: any) {
    // Execute frontend automations (add_followers, notification, update_stage)
    await this.executeFollowerAutomations({
      type: 'project_created',
      project,
      metadata: {
        pipeline_id: project.pipeline_id,
        user_id: project.created_by
      }
    });

    // Also call backend for email actions (backend handles email via Postmark)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const tenantId = await getCurrentTenantId();

      await apiClient.post('/api/v1/automation/webhook/project-created', {
        project_id: project.id,
        pipeline_id: project.pipeline_id,
        user_id: user?.id,
        tenant_id: tenantId
      });
    } catch (error) {
      console.error('Error calling backend project-created webhook:', error);
    }
  }

  /**
   * Trigger follower automations when a project is assigned to a team
   */
  async onProjectAssigned(project: any, teamId: string) {
    // Execute frontend automations (add_followers, notification, update_stage)
    await this.executeFollowerAutomations({
      type: 'project_assigned',
      project,
      metadata: {
        team_id: teamId,
        pipeline_id: project.pipeline_id
      }
    });

    // Also call backend for email actions (backend handles email via Postmark)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const tenantId = await getCurrentTenantId();

      await apiClient.post('/api/v1/automation/webhook/project-assigned', {
        project_id: project.id,
        team_id: teamId,
        pipeline_id: project.pipeline_id,
        user_id: user?.id,
        tenant_id: tenantId
      });
    } catch (error) {
      console.error('Error calling backend project-assigned webhook:', error);
    }
  }

  /**
   * Trigger follower automations when a project moves stages
   */
  async onStageChanged(project: any, fromStageId: number, toStageId: number) {
    // Execute frontend automations (add_followers, notification, update_stage)
    await this.executeFollowerAutomations({
      type: 'stage_changed',
      project,
      metadata: {
        from_stage_id: fromStageId,
        to_stage_id: toStageId,
        pipeline_id: project.pipeline_id
      }
    });

    // Also call backend for email actions (backend handles email via Postmark)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const tenantId = await getCurrentTenantId();

      await apiClient.post('/api/v1/automation/webhook/stage-changed', {
        project_id: project.id,
        from_stage_id: fromStageId,
        to_stage_id: toStageId,
        user_id: user?.id,
        tenant_id: tenantId
      });
    } catch (error) {
      // Log but don't fail - frontend automations already executed
      console.error('Error calling backend stage-changed webhook:', error);
    }
  }

  /**
   * Trigger follower automations when a project enters a pipeline
   */
  async onPipelineEntered(project: any, pipelineId: number) {
    // Execute frontend automations (add_followers, notification, update_stage)
    await this.executeFollowerAutomations({
      type: 'pipeline_entered',
      project,
      metadata: {
        pipeline_id: pipelineId
      }
    });

    // Also call backend for email actions (backend handles email via Postmark)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const tenantId = await getCurrentTenantId();

      await apiClient.post('/api/v1/automation/webhook/pipeline-entered', {
        project_id: project.id,
        pipeline_id: pipelineId,
        user_id: user?.id,
        tenant_id: tenantId
      });
    } catch (error) {
      console.error('Error calling backend pipeline-entered webhook:', error);
    }
  }

  /**
   * Trigger automations when a design request is created
   */
  async onDesignRequestCreated(project: any, designRequestId: string, requestType: 'residential' | 'commercial') {
    // Execute frontend automations (add_followers, notification, update_stage)
    await this.executeFollowerAutomations({
      type: 'design_request_created',
      project,
      metadata: {
        design_request_id: designRequestId,
        design_request_type: requestType,
        user_id: project.created_by
      }
    });

    // Also call backend for email actions (backend handles email via Postmark)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const tenantId = await getCurrentTenantId();

      await apiClient.post('/api/v1/automation/webhook/design-request-created', {
        project_id: project.id,
        design_request_id: designRequestId,
        request_type: requestType,
        pipeline_id: project.pipeline_id,
        user_id: user?.id,
        tenant_id: tenantId
      });
    } catch (error) {
      console.error('Error calling backend design-request-created webhook:', error);
    }
  }

  /**
   * Trigger automations when a design request status changes
   */
  async onDesignRequestStatusChanged(project: any, designRequestId: string, status: string) {
    // Execute frontend automations (add_followers, notification, update_stage)
    await this.executeFollowerAutomations({
      type: 'design_request_status_changed',
      project,
      metadata: {
        design_request_id: designRequestId,
        design_request_status: status
      }
    });

    // Also call backend for email actions (backend handles email via Postmark)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const tenantId = await getCurrentTenantId();

      await apiClient.post('/api/v1/automation/webhook/design-request-status-changed', {
        project_id: project.id,
        design_request_id: designRequestId,
        status: status,
        pipeline_id: project.pipeline_id,
        user_id: user?.id,
        tenant_id: tenantId
      });
    } catch (error) {
      console.error('Error calling backend design-request-status-changed webhook:', error);
    }
  }
}

// Export singleton instance
export const followerAutomationService = new FollowerAutomationService();